import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Container, Row, Col, Spinner, Card, Table, ListGroup, Form, Button, Badge } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  DoughnutController
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, 
  startOfYear, endOfYear, eachMonthOfInterval, addYears, subYears } from 'date-fns';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import enUS from 'date-fns/locale/en-US';
import itIT from 'date-fns/locale/it';
import zhCN from 'date-fns/locale/zh-CN';
import StatCard from './StatCard';
import {
  faTools,
  faCheck,
  faHandshake,
  faBoxes,
  faMoneyBill,
  faExclamationTriangle,
  faChevronLeft,
  faChevronRight,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { db, auth } from '../../services/firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './MonthPicker.scss';
import './Statistics.scss';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import settingsService from '../../services/settingsService';
import { formatCurrency } from '../../utils/formatCurrency';

// Registra i componenti Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  DoughnutController,
  Title,
  Tooltip,
  Legend
);

// Registra le localizzazioni
registerLocale('en', enUS);
registerLocale('it', itIT);
registerLocale('zh', zhCN);

function Statistics() {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    inRepair: { count: 0, revenue: 0 },
    repaired: { count: 0, revenue: 0 },
    delivered: { count: 0, revenue: 0 },
    orders: { count: 0, revenue: 0 },
    paid: { count: 0, revenue: 0 },
    unpaid: { count: 0, revenue: 0 },
    devicesByBrand: {},
    mediaGiorni: 0,
    total: 0,
    completionRate: 0,
    paymentRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [dailyStats, setDailyStats] = useState({
    labels: [],
  });
  const [devices, setDevices] = useState([]);
  const [settings, setSettings] = useState({ currency: 'EUR' });
  const [view, setView] = useState('daily');
  const [brandChartData, setBrandChartData] = useState({ chartData: null, brandList: [], otherBrands: [] });

  // Aggiorna il locale in base alla lingua corrente
  const currentLocale = (() => {
    switch (i18n.language) {
      case 'it': return itIT;
      case 'zh': return zhCN;
      default: return enUS;
    }
  })();

  useEffect(() => {
    const checkAuth = async () => {
      if (!user) {
        navigate('/login');
        return;
      }
      await fetchStatistics();
    };
    
    checkAuth();
  }, [user, navigate]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await settingsService.get();
        setSettings(data);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    if (user) {
      loadSettings();
    }
  }, [user]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('Utente non autenticato');

      const devicesRef = collection(db, `users/${userId}/devices`);
      const q = query(devicesRef, orderBy('createdAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const fetchedDevices = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Salva i dispositivi nello state
      setDevices(fetchedDevices);

      // Calcola tutte le statistiche
      const basicStats = {
        total: fetchedDevices.length,
        inRiparazione: fetchedDevices.filter(d => d.status === 'inRiparazione').length,
        riparati: fetchedDevices.filter(d => d.status === 'riparato').length,
        consegnati: fetchedDevices.filter(d => d.status === 'restituito').length,
        ricavi: fetchedDevices.reduce((acc, d) => acc + (d.price || 0), 0),
        mediaGiorni: calculateAverageDays(fetchedDevices),
        devicesByBrand: calculateDevicesByBrand(fetchedDevices),
        monthlyStats: calculateMonthlyTrends(fetchedDevices)
      };

      // Calcola le statistiche per le card
      const cardStats = calculateStats(fetchedDevices);

      // Combina tutte le statistiche
      setStats({
        ...basicStats,
        ...cardStats
      });
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
      setError('Si è verificato un errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (devices) => {
    const baseStats = devices.reduce((acc, device) => {
      const price = Number(device.price) || 0;

      // Inizializza gli oggetti se non esistono
      acc.inRepair = acc.inRepair || { count: 0, revenue: 0 };
      acc.repaired = acc.repaired || { count: 0, revenue: 0 };
      acc.delivered = acc.delivered || { count: 0, revenue: 0 };
      acc.orders = acc.orders || { count: 0, revenue: 0 };
      acc.nonRepairable = acc.nonRepairable || { count: 0, revenue: 0 };
      acc.paid = acc.paid || { count: 0, revenue: 0 };
      acc.unpaid = acc.unpaid || { count: 0, revenue: 0 };
      acc.notRequired = acc.notRequired || { count: 0, revenue: 0 };

      // Gestione stati dispositivo
      if (device.status === 'inRiparazione') {
        acc.inRepair.count++;
        acc.inRepair.revenue += price;
      } else if (device.status === 'riparato') {
        acc.repaired.count++;
        acc.repaired.revenue += price;
      } else if (device.status === 'restituito') {
        acc.delivered.count++;
        acc.delivered.revenue += price;
      } else if (device.status === 'ordine') {
        acc.orders.count++;
        acc.orders.revenue += price;
      } else if (device.status === 'nonRiparabile') {
        acc.nonRepairable.count++;
        acc.nonRepairable.revenue += price;
      }

      // Gestione stati pagamento
      if (device.paymentStatus === 'pagato') {
        acc.paid.count++;
        acc.paid.revenue += price;
      } else if (device.paymentStatus === 'nonRichiesto') {
        acc.notRequired.count++;
        acc.notRequired.revenue += price;
      } else {
        acc.unpaid.count++;
        acc.unpaid.revenue += price;
      }

      // Aggiungi statistiche per marca
      const brand = device.brand || 'Altro';
      if (!acc.devicesByBrand[brand]) {
        acc.devicesByBrand[brand] = { count: 0, revenue: 0 };
      }
      acc.devicesByBrand[brand].count++;
      acc.devicesByBrand[brand].revenue += price;

      return acc;
    }, {
      inRepair: { count: 0, revenue: 0 },
      repaired: { count: 0, revenue: 0 },
      delivered: { count: 0, revenue: 0 },
      orders: { count: 0, revenue: 0 },
      paid: { count: 0, revenue: 0 },
      unpaid: { count: 0, revenue: 0 },
      devicesByBrand: {},
      total: devices.length,
      mediaGiorni: calculateAverageDays(devices)
    });

    // Calcola i tassi
    baseStats.completionRate = (baseStats.repaired.count / devices.length) * 100;
    baseStats.paymentRate = (baseStats.paid.count / baseStats.repaired.count) * 100;

    return baseStats;
  };

  // Funzione helper per calcolare la media dei giorni di riparazione
  const calculateAverageDays = (devices) => {
    const completedDevices = devices.filter(d => 
      d.status === 'riparato' || d.status === 'restituito'
    );
    
    if (completedDevices.length === 0) return 0;

    const totalDays = completedDevices.reduce((acc, device) => {
      const start = device.createdAt?.toDate();
      const end = device.updatedAt?.toDate();
      if (!start || !end) return acc;
      return acc + Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    }, 0);

    return Math.round(totalDays / completedDevices.length);
  };

  // Funzione helper per calcolare le statistiche per marca
  const calculateDevicesByBrand = (devices) => {
    return devices.reduce((acc, device) => {
      const brand = device.brand || 'Altro';
      acc[brand] = (acc[brand] || 0) + 1;
      return acc;
    }, {});
  };

  // Funzione helper per calcolare le tendenze mensili
  const calculateMonthlyTrends = (devices) => {
    return devices.reduce((acc, device) => {
      const date = device.createdAt?.toDate();
      if (!date) return acc;
      
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      if (!acc[monthYear]) {
        acc[monthYear] = {
          count: 0,
          revenue: 0
        };
      }
      
      acc[monthYear].count++;
      acc[monthYear].revenue += device.price || 0;
      
      return acc;
    }, {});
  };

  const formatMonetary = (amount) => {
    return formatCurrency(amount, settings.currency);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    if (view === 'daily') {
      setSelectedMonth(subMonths(selectedMonth, 1));
    } else {
      setSelectedMonth(subYears(selectedMonth, 1));
    }
  };

  const handleNextMonth = () => {
    if (view === 'daily') {
      setSelectedMonth(addMonths(selectedMonth, 1));
    } else {
      setSelectedMonth(addYears(selectedMonth, 1));
    }
  };

  const isCurrentPeriod = () => {
    if (view === 'daily') {
      return format(selectedMonth, 'yyyy-MM') === format(new Date(), 'yyyy-MM');
    } else {
      return format(selectedMonth, 'yyyy') === format(new Date(), 'yyyy');
    }
  };

  const handleViewChange = () => {
    setView(view === 'daily' ? 'monthly' : 'daily');
  };

  const CustomInput = forwardRef(({ value, onClick }, ref) => (
    <Button
      ref={ref}
      size="sm"
      variant="light"
      className="chart-control-btn month-display"
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faCalendar} className="me-2" />
      {view === 'daily' 
        ? format(selectedMonth, 'MMMM yyyy', { locale: currentLocale })
        : format(selectedMonth, 'yyyy', { locale: currentLocale })}
    </Button>
  ));

  // Funzione per calcolare le statistiche giornaliere
  const calculateDailyStats = (devices, month) => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    const days = eachDayOfInterval({ start, end });

    // Inizializza l'oggetto per i dati giornalieri
    const dailyData = days.reduce((acc, day) => {
      const dayStr = format(day, 'yyyy-MM-dd');
      acc[dayStr] = {
        revenues: 0,
        newDevices: 0,    // Aggiungi contatore dispositivi nuovi
        returnedDevices: 0 // Aggiungi contatore dispositivi restituiti
      };
      return acc;
    }, {});

    // Inizializza i totali mensili
    let monthlyTotals = {
      revenues: 0,
      newDevices: 0,
      returnedDevices: 0
    };

    // Calcola i dati per ogni dispositivo
    devices.forEach(device => {
      const createdAt = device.createdAt?.toDate();
      const updatedAt = device.updatedAt?.toDate();
      const deposit = Number(device.deposit) || 0;
      const price = Number(device.price) || 0;

      // Conta i dispositivi nuovi
      if (createdAt) {
        const createdDay = format(createdAt, 'yyyy-MM-dd');
        if (dailyData[createdDay]) {
          dailyData[createdDay].newDevices++;
          monthlyTotals.newDevices++;
          
          // Gestione dei pagamenti alla creazione
          if (device.paymentStatus === 'pagato' && device.status !== 'restituito') {
            // Se il dispositivo è stato pagato completamente all'inizio ma non ancora restituito
            dailyData[createdDay].revenues += price;
            monthlyTotals.revenues += price;
          } else if (device.paymentStatus === 'pagato' && device.status === 'restituito' && updatedAt) {
            // Se il dispositivo è stato pagato e restituito, verifica se è avvenuto nello stesso giorno
            const updatedDay = format(updatedAt, 'yyyy-MM-dd');
            if (updatedDay === createdDay) {
              // Se sono nello stesso giorno, aggiungi tutto il prezzo qui
              dailyData[createdDay].revenues += price;
              monthlyTotals.revenues += price;
            } else {
              // Se sono in giorni diversi, aggiungi solo l'acconto
              dailyData[createdDay].revenues += deposit;
              monthlyTotals.revenues += deposit;
            }
          } else if (deposit > 0) {
            // Solo acconto per dispositivi con pagamento non completo
            dailyData[createdDay].revenues += deposit;
            monthlyTotals.revenues += deposit;
          }
        }
      }

      // Conta i dispositivi restituiti e gestisci i pagamenti
      if (updatedAt && device.status === 'restituito') {
        const updatedDay = format(updatedAt, 'yyyy-MM-dd');
        if (dailyData[updatedDay]) {
          dailyData[updatedDay].returnedDevices++;
          monthlyTotals.returnedDevices++;
          
          // Gestione pagamenti alla restituzione
          // Verifica se il dispositivo NON è stato già pagato completamente all'inizio
          // e NON è stato conteggiato nello stesso giorno di creazione
          if (device.paymentStatus === 'pagato' && createdAt) {
            const createdDay = format(createdAt, 'yyyy-MM-dd');
            
            // Serve capire se il pagamento è già stato conteggiato
            const isPaidAtCreation = updatedDay === createdDay || 
              (device.paymentCompleted && device.paymentCompletedDate);
            
            if (!isPaidAtCreation) {
              // Verifica se esiste un campo che indica quando è stato completato il pagamento
              if (device.paymentCompletedDate) {
                // Se esiste una data di pagamento completato, confrontala con la data di restituzione
                const paymentDate = device.paymentCompletedDate.toDate();
                const paymentDay = format(paymentDate, 'yyyy-MM-dd');
                
                // Se il pagamento è stato completato nello stesso giorno della restituzione
                if (paymentDay === updatedDay) {
                  const remainingPayment = price - deposit;
                  if (remainingPayment > 0) {
                    dailyData[updatedDay].revenues += remainingPayment;
                    monthlyTotals.revenues += remainingPayment;
                  }
                }
              } else {
                // Comportamento precedente se non abbiamo informazioni sulla data di pagamento
                // Aggiungi solo se è un giorno diverso da quello di creazione
                if (updatedDay !== createdDay) {
                  const remainingPayment = price - deposit;
                  if (remainingPayment > 0) {
                    dailyData[updatedDay].revenues += remainingPayment;
                    monthlyTotals.revenues += remainingPayment;
                  }
                }
              }
            }
          }
        }
      }
    });

    // Prepara i dati per il grafico
    return {
      labels: days.map(day => format(day, 'd', { locale: currentLocale })),
      revenues: Object.values(dailyData).map(d => d.revenues),
      returnedDevices: Object.values(dailyData).map(d => d.returnedDevices),
      newDevices: Object.values(dailyData).map(d => d.newDevices),
      monthlyTotals: monthlyTotals
    };
  };

  // Funzione per calcolare le statistiche mensili per anno
  const calculateYearlyStats = (devices, year) => {
    const start = startOfYear(year);
    const end = endOfYear(year);
    const months = eachMonthOfInterval({ start, end });

    // Inizializza l'oggetto per i dati mensili
    const monthlyData = months.reduce((acc, month) => {
      const monthStr = format(month, 'yyyy-MM');
      acc[monthStr] = {
        revenues: 0,
        newDevices: 0,
        returnedDevices: 0
      };
      return acc;
    }, {});

    // Inizializza i totali annuali
    let yearlyTotals = {
      revenues: 0,
      newDevices: 0,
      returnedDevices: 0
    };

    // Calcola i dati per ogni dispositivo
    devices.forEach(device => {
      const createdAt = device.createdAt?.toDate();
      const updatedAt = device.updatedAt?.toDate();
      const deposit = Number(device.deposit) || 0;
      const price = Number(device.price) || 0;

      // Conta i dispositivi nuovi
      if (createdAt) {
        const createdMonth = format(createdAt, 'yyyy-MM');
        if (monthlyData[createdMonth]) {
          monthlyData[createdMonth].newDevices++;
          yearlyTotals.newDevices++;
          
          // Gestione dei pagamenti alla creazione
          if (device.paymentStatus === 'pagato' && device.status !== 'restituito') {
            // Se il dispositivo è stato pagato completamente all'inizio ma non ancora restituito
            monthlyData[createdMonth].revenues += price;
            yearlyTotals.revenues += price;
          } else if (device.paymentStatus === 'pagato' && device.status === 'restituito' && updatedAt) {
            // Se il dispositivo è stato pagato e restituito, verifica se è avvenuto nello stesso mese
            const updatedMonth = format(updatedAt, 'yyyy-MM');
            if (updatedMonth === createdMonth) {
              // Se sono nello stesso mese, aggiungi tutto il prezzo qui
              monthlyData[createdMonth].revenues += price;
              yearlyTotals.revenues += price;
            } else {
              // Se sono in mesi diversi, aggiungi solo l'acconto
              monthlyData[createdMonth].revenues += deposit;
              yearlyTotals.revenues += deposit;
            }
          } else if (deposit > 0) {
            // Solo acconto per dispositivi con pagamento non completo
            monthlyData[createdMonth].revenues += deposit;
            yearlyTotals.revenues += deposit;
          }
        }
      }

      // Conta i dispositivi restituiti e gestisci i pagamenti
      if (updatedAt && device.status === 'restituito') {
        const updatedMonth = format(updatedAt, 'yyyy-MM');
        if (monthlyData[updatedMonth]) {
          monthlyData[updatedMonth].returnedDevices++;
          yearlyTotals.returnedDevices++;
          
          // Gestione pagamenti alla restituzione
          // Verifica se il dispositivo NON è stato già pagato completamente all'inizio
          // e NON è stato conteggiato nello stesso mese di creazione
          if (device.paymentStatus === 'pagato' && createdAt) {
            const createdMonth = format(createdAt, 'yyyy-MM');
            
            // Serve capire se il pagamento è già stato conteggiato
            const isPaidAtCreation = updatedMonth === createdMonth || 
              (device.paymentCompleted && device.paymentCompletedDate);
              
            if (!isPaidAtCreation) {
              // Verifica se esiste un campo che indica quando è stato completato il pagamento
              if (device.paymentCompletedDate) {
                // Se esiste una data di pagamento completato, confrontala con la data di restituzione
                const paymentDate = device.paymentCompletedDate.toDate();
                const paymentMonth = format(paymentDate, 'yyyy-MM');
                
                // Se il pagamento è stato completato nello stesso mese della restituzione
                if (paymentMonth === updatedMonth) {
                  const remainingPayment = price - deposit;
                  if (remainingPayment > 0) {
                    monthlyData[updatedMonth].revenues += remainingPayment;
                    yearlyTotals.revenues += remainingPayment;
                  }
                }
              } else {
                // Comportamento precedente se non abbiamo informazioni sulla data di pagamento
                // Aggiungi solo se è un mese diverso da quello di creazione
                if (updatedMonth !== createdMonth) {
                  const remainingPayment = price - deposit;
                  if (remainingPayment > 0) {
                    monthlyData[updatedMonth].revenues += remainingPayment;
                    yearlyTotals.revenues += remainingPayment;
                  }
                }
              }
            }
          }
        }
      }
    });

    // Prepara i dati per il grafico
    return {
      labels: months.map(month => format(month, 'MMM', { locale: currentLocale })),
      revenues: Object.values(monthlyData).map(d => d.revenues),
      returnedDevices: Object.values(monthlyData).map(d => d.returnedDevices),
      newDevices: Object.values(monthlyData).map(d => d.newDevices),
      monthlyTotals: yearlyTotals
    };
  };

  // Aggiungi questo useEffect per aggiornare i dati del grafico
  useEffect(() => {
    if (!devices.length) return;
    const stats = view === 'daily' 
      ? calculateDailyStats(devices, selectedMonth)
      : calculateYearlyStats(devices, selectedMonth);
    setDailyStats(stats);
  }, [selectedMonth, devices, currentLocale, view]);

  // Aggiungi un useEffect per preparare i dati del grafico a ciambella
  useEffect(() => {
    if (stats?.devicesByBrand && stats.total > 0) {
      const data = prepareBrandChartData(stats.devicesByBrand, stats.total);
      setBrandChartData(data);
    }
  }, [stats.devicesByBrand, stats.total]);

  // Aggiungi le traduzioni per le etichette del selettore di vista
  const getChartTitle = () => {
    return view === 'daily' 
      ? t('statistics.dailyStats.title') 
      : t('statistics.monthlyStats.title');
  };

  // Aggiungi questa funzione per preparare i dati del grafico a ciambella
  const prepareBrandChartData = (devicesByBrand, total) => {
    // Determina quali marchi includere nella categoria "Altro"
    // Consideriamo come soglia il 3% del totale
    const threshold = Math.max(1, Math.floor(total * 0.03));
    
    // Ordina i marchi per quantità decrescente
    const sortedBrands = Object.entries(devicesByBrand)
      .map(([brand, data]) => ({
        brand,
        count: data.count,
        revenue: data.revenue,
        percentage: (data.count / total) * 100
      }))
      .sort((a, b) => b.count - a.count);
    
    // Separa i marchi principali da quelli con pochi dispositivi
    const mainBrands = [];
    const otherBrands = [];
    
    sortedBrands.forEach(brand => {
      if (brand.count >= threshold) {
        mainBrands.push(brand);
      } else {
        otherBrands.push(brand);
      }
    });
    
    // Crea la categoria "Altro" combinando i marchi minori
    const otherCategory = otherBrands.reduce(
      (acc, brand) => {
        acc.count += brand.count;
        acc.revenue += brand.revenue;
        acc.brands.push(brand);
        return acc;
      },
      { brand: 'Altro', count: 0, revenue: 0, brands: [], percentage: 0 }
    );
    
    // Calcola la percentuale per la categoria "Altro"
    if (otherCategory.count > 0) {
      otherCategory.percentage = (otherCategory.count / total) * 100;
      mainBrands.push(otherCategory);
    }
    
    // Definisci i colori base
    const baseColors = [
      { bg: 'rgba(54, 162, 235, 0.8)', border: 'rgba(54, 162, 235, 1)' },
      { bg: 'rgba(255, 99, 132, 0.8)', border: 'rgba(255, 99, 132, 1)' },
      { bg: 'rgba(255, 206, 86, 0.8)', border: 'rgba(255, 206, 86, 1)' },
      { bg: 'rgba(75, 192, 192, 0.8)', border: 'rgba(75, 192, 192, 1)' },
      { bg: 'rgba(153, 102, 255, 0.8)', border: 'rgba(153, 102, 255, 1)' },
      { bg: 'rgba(255, 159, 64, 0.8)', border: 'rgba(255, 159, 64, 1)' },
      { bg: 'rgba(199, 199, 199, 0.8)', border: 'rgba(199, 199, 199, 1)' },
      { bg: 'rgba(83, 102, 255, 0.8)', border: 'rgba(83, 102, 255, 1)' },
      { bg: 'rgba(40, 167, 69, 0.8)', border: 'rgba(40, 167, 69, 1)' },
      { bg: 'rgba(220, 53, 69, 0.8)', border: 'rgba(220, 53, 69, 1)' },
    ];
    
    // Funzione per generare colori aggiuntivi
    const generateColor = (index) => {
      // Genera un colore basato sull'indice per assicurare che sia ripetibile
      const hue = (index * 137.5) % 360;
      return {
        bg: `hsla(${hue}, 70%, 60%, 0.8)`,
        border: `hsla(${hue}, 70%, 60%, 1)`
      };
    };
    
    // Genera abbastanza colori per tutti i marchi
    const colors = [];
    for (let i = 0; i < mainBrands.length; i++) {
      if (i < baseColors.length) {
        colors.push(baseColors[i]);
      } else {
        colors.push(generateColor(i));
      }
    }
    
    // Prepara i dati per il grafico
    const chartData = {
      labels: mainBrands.map(b => b.brand),
      datasets: [
        {
          data: mainBrands.map(b => b.count),
          backgroundColor: colors.map(c => c.bg),
          borderColor: colors.map(c => c.border),
          borderWidth: 1,
        },
      ],
    };
    
    return { chartData, brandList: mainBrands, otherBrands };
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <Container fluid className="py-4">
      <h1 className="h3 mb-4">{t('statistics.title')}</h1>
      
      <Row className="g-4 mb-4">
        <Col lg={3} md={4} sm={6}>
          <StatCard
            title={t('statistics.cards.inRepair')}
            count={stats.inRepair.count}
            revenue={stats.inRepair.revenue}
            icon={faTools}
            color="warning"
            currency={settings.currency}
          />
        </Col>
        <Col lg={3} md={4} sm={6}>
          <StatCard
            title={t('statistics.cards.repaired')}
            count={stats.repaired.count}
            revenue={stats.repaired.revenue}
            icon={faCheck}
            color="success"
            currency={settings.currency}
          />
        </Col>
        <Col lg={3} md={4} sm={6}>
          <StatCard
            title={t('statistics.cards.paid')}
            count={stats.paid.count}
            revenue={stats.paid.revenue}
            icon={faMoneyBill}
            color="success"
            currency={settings.currency}
          />
        </Col>
        <Col lg={3} md={4} sm={6}>
          <StatCard
            title={t('statistics.cards.unpaid')}
            count={stats.unpaid.count}
            revenue={stats.unpaid.revenue}
            icon={faExclamationTriangle}
            color="danger"
            currency={settings.currency}
          />
        </Col>
      </Row>

      <Row className="mt-4 mb-4">
        <Col xs={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="mb-0">{getChartTitle()}</h5>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <div className="chart-controls">
                  <Form.Check 
                    type="switch"
                    id="view-switch"
                    label={view === 'daily' ? t('statistics.viewDaily') : t('statistics.viewMonthly')}
                    checked={view === 'monthly'}
                    onChange={handleViewChange}
                    className="me-3"
                  />
                  <Button
                    size="sm"
                    variant="light"
                    className="chart-control-btn nav-btn"
                    onClick={handlePrevMonth}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>

                  <DatePicker
                    selected={selectedMonth}
                    onChange={handleMonthChange}
                    dateFormat={view === 'daily' ? "MM/yyyy" : "yyyy"}
                    showMonthYearPicker={view === 'daily'}
                    showYearPicker={view === 'monthly'}
                    showFullMonthYearPicker={view === 'daily'}
                    showTwoColumnMonthYearPicker={view === 'daily'}
                    customInput={<CustomInput />}
                    open={isOpen}
                    onClickOutside={() => setIsOpen(false)}
                    onInputClick={() => setIsOpen(true)}
                    locale={currentLocale}
                    maxDate={new Date()}
                    minDate={new Date(2020, 0)}
                  />

                  <Button
                    size="sm"
                    variant="light"
                    className="chart-control-btn nav-btn"
                    onClick={handleNextMonth}
                    disabled={isCurrentPeriod()}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '400px' }}>
                <Bar
                  data={{
                    labels: dailyStats.labels || [],
                    datasets: [
                      {
                        label: t('statistics.chart.datasets.newDevices'),
                        data: dailyStats.newDevices || [],
                        backgroundColor: 'rgba(255, 193, 7, 0.5)',
                        borderColor: 'rgb(255, 193, 7)',
                        borderWidth: 1,
                        yAxisID: 'y-devices'
                      },
                      {
                        label: t('statistics.chart.datasets.returnedDevices'),
                        data: dailyStats.returnedDevices || [],
                        backgroundColor: 'rgba(23, 162, 184, 0.5)',
                        borderColor: 'rgb(23, 162, 184)',
                        borderWidth: 1,
                        yAxisID: 'y-devices'
                      },
                      {
                        label: t('statistics.chart.datasets.revenues'),
                        data: dailyStats.revenues || [],
                        backgroundColor: 'rgba(40, 167, 69, 0.5)',
                        borderColor: 'rgb(40, 167, 69)',
                        borderWidth: 1,
                        yAxisID: 'y-revenues'
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: 'top'
                      },
                      title: {
                        display: true,
                        text: [
                          view === 'daily'
                            ? `${t('statistics.chart.dailyTrend')} - ${format(selectedMonth, 'MMMM yyyy', { locale: currentLocale })}`
                            : `${t('statistics.chart.monthlyTrend')} - ${format(selectedMonth, 'yyyy', { locale: currentLocale })}`,
                          `${t('statistics.chart.datasets.newDevices')}: ${dailyStats.monthlyTotals?.newDevices || 0} | ${t('statistics.chart.datasets.returnedDevices')}: ${dailyStats.monthlyTotals?.returnedDevices || 0} | ${t('statistics.chart.datasets.revenues')}: ${formatMonetary(dailyStats.monthlyTotals?.revenues || 0)}`
                        ]
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            if (context.dataset.yAxisID === 'y-revenues') {
                              return `${context.dataset.label}: ${formatMonetary(context.raw)}`;
                            }
                            return `${context.dataset.label}: ${context.raw}`;
                          }
                        }
                      }
                    },
                    scales: {
                      'y-devices': {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: t('statistics.chart.axes.devices')
                        },
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      },
                      'y-revenues': {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: t('statistics.chart.axes.revenues')
                        },
                        beginAtZero: true,
                        grid: {
                          drawOnChartArea: false
                        },
                        ticks: {
                          callback: function(value) {
                            return formatMonetary(value);
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Header className="bg-light">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faBoxes} className="me-2 text-primary" />
                <h5 className="mb-0">{t('statistics.brandStats.title')}</h5>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col lg={7} md={12} className="d-flex justify-content-center align-items-center">
                  <div style={{ height: '260px', width: '260px' }}>
                    {brandChartData.chartData && (
                      <Doughnut
                        data={brandChartData.chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const label = context.label || '';
                                  const value = context.raw || 0;
                                  const percentage = (value / stats.total * 100).toFixed(1);
                                  return `${label}: ${value} (${percentage}%)`;
                                }
                              }
                            }
                          },
                          cutout: '65%'
                        }}
                      />
                    )}
                  </div>
                </Col>
                <Col lg={5} md={12} className="mt-lg-0 mt-4">
                  <div className="brand-legend">
                    <h6 className="text-center text-lg-start mb-3">{t('statistics.brandStats.distribution')}</h6>
                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                      {brandChartData.brandList.map((brand, index) => (
                        <div key={brand.brand} className="d-flex flex-column mb-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <div 
                                className="color-box me-2" 
                                style={{ 
                                  width: '12px', 
                                  height: '12px', 
                                  backgroundColor: brandChartData.chartData.datasets[0].backgroundColor[index],
                                  borderRadius: '2px'
                                }}
                              />
                              <span className="small fw-medium">{brand.brand}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <Badge bg="primary" pill className="me-2">
                                {brand.count}
                              </Badge>
                              <span className="small text-muted">{brand.percentage.toFixed(1)}%</span>
                            </div>
                          </div>
                          <div className="small text-success ms-4 mt-1">
                            {t('statistics.brandStats.revenues')}: {formatMonetary(brand.revenue)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="overflow-hidden">
            <Card.Header className="bg-light">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faMoneyBill} className="me-2 text-success" />
                <h5 className="mb-0">{t('statistics.financialSummary.title', 'Riepilogo Finanziario')}</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush" className="rounded-bottom">
                <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                  <div>
                    <h6 className="mb-0">{t('statistics.financialSummary.totalRevenue', 'Ricavi Totali')}</h6>
                    <small className="text-muted">{t('statistics.financialSummary.allTimeRevenue', 'Tutti i ricavi generati')}</small>
                  </div>
                  <h4 className="mb-0 text-success">
                    {formatMonetary(stats.paid.revenue)}
                  </h4>
                </ListGroup.Item>
                
                <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                  <div>
                    <h6 className="mb-0">{t('statistics.financialSummary.pendingRevenue', 'Ricavi in Attesa')}</h6>
                    <small className="text-muted">{t('statistics.financialSummary.unpaidRepairs', 'Riparazioni completate non pagate')}</small>
                  </div>
                  <h4 className="mb-0 text-warning">
                    {formatMonetary(stats.unpaid.revenue)}
                  </h4>
                </ListGroup.Item>
                
                <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                  <div>
                    <h6 className="mb-0">{t('statistics.financialSummary.averageRepairValue', 'Valore Medio Riparazione')}</h6>
                    <small className="text-muted">{t('statistics.financialSummary.perRepair', 'Ricavo medio per riparazione')}</small>
                  </div>
                  <h4 className="mb-0 text-primary">
                    {formatMonetary(stats.repaired.count > 0 ? stats.repaired.revenue / stats.repaired.count : 0)}
                  </h4>
                </ListGroup.Item>
                
                <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                  <div>
                    <h6 className="mb-0">{t('statistics.financialSummary.monthlyAverage', 'Media Mensile')}</h6>
                    <small className="text-muted">{t('statistics.financialSummary.estimatedMonthly', 'Stima ricavi mensili')}</small>
                  </div>
                  <h4 className="mb-0 text-info">
                    {formatMonetary(dailyStats.monthlyTotals?.revenues || 0)}
                  </h4>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Statistics; 